//angular
import { Platform } from '@angular/cdk/platform';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

//msal
import { RedirectRequest } from '@azure/msal-browser';

//local
import { ApplicationConfiguration } from '@core/configuration';
import { AuthRouteHelper, AuthRouteIntent, AuthRouteState, ClientInfo } from '../helpers';

@Injectable({
    providedIn: 'root'
})
export class AuthRequestBuilderService {
    private readonly forcePromptLoginStorageKey = 'auth.forcePromptLogin';

    constructor(
        private applicationConfig: ApplicationConfiguration,
        private platform: Platform,
        private client: ClientInfo,
        private authRouteHelper: AuthRouteHelper,
        private router: Router,
    ) { }

    public createRequestMetadata(): Pick<RedirectRequest, 'correlationId' | 'extraQueryParameters'> {
        const correlationId = this.createCorrelationId();
        return {
            correlationId,
            extraQueryParameters: this.createExtraQueryParameters(correlationId),
        };
    }

    public createRedirectRequest(scopes: string[], authority?: string): RedirectRequest {
        const metadata = this.createRequestMetadata();

        const request: RedirectRequest = {
            scopes,
            ...metadata,
        };

        if (this.shouldForcePromptLogin()) {
            request.prompt = 'login';
        }

        if (authority) {
            request.authority = authority;
        }

        return request;
    }

    public createSwitchTenantRedirectRequest(
        tenantId: string,
        idTokenHint: string,
        tenantSlugForRoute: string | undefined,
        switchTenantAuthority: string,
        readScopes: string[],
        writeScopes: string[],
    ): RedirectRequest {
        const scopes = [
            'openid',
            'profile',
            'email',
            ...readScopes,
            ...writeScopes,
        ];

        const request = this.createRedirectRequest(scopes, switchTenantAuthority);
        request.extraQueryParameters = {
            ...(request.extraQueryParameters || {}),
            tenant: tenantId,
            id_token_hint: idTokenHint,
        };

        const routeTenant = (tenantSlugForRoute || tenantId || '').trim();
        const redirectStartPage = this.resolveSwitchTenantRedirectStartPage(routeTenant);
        if (redirectStartPage) {
            request.redirectStartPage = redirectStartPage;
        }

        return request;
    }

    public createLoginRedirectRequest(readScopes: string[], writeScopes: string[]): RedirectRequest {
        const scopes = [
            'openid',
            'profile',
            'email',
            ...readScopes,
            ...writeScopes,
        ];

        return this.createRedirectRequest(scopes);
    }

    public setForcePromptLogin(force: boolean): void {
        if (!this.platform.isBrowser || typeof sessionStorage === 'undefined') {
            return;
        }

        if (force) {
            sessionStorage.setItem(this.forcePromptLoginStorageKey, 'true');
            return;
        }

        sessionStorage.removeItem(this.forcePromptLoginStorageKey);
    }

    public clearForcePromptLogin(): void {
        this.setForcePromptLogin(false);
    }

    private shouldForcePromptLogin(): boolean {
        if (!this.platform.isBrowser || typeof sessionStorage === 'undefined') {
            return false;
        }

        return sessionStorage.getItem(this.forcePromptLoginStorageKey) === 'true';
    }

    private createCorrelationId(): string {
        if (this.platform.isBrowser && typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
            const random = Math.random() * 16 | 0;
            const value = character === 'x' ? random : (random & 0x3 | 0x8);
            return value.toString(16);
        });
    }

    private resolveCurrentAuthRouteState(): AuthRouteState {
        const currentUrl = (this.router.url || '/').trim() || '/';
        return this.authRouteHelper.resolveRouteState(currentUrl);
    }

    private createExtraQueryParameters(correlationId: string): Record<string, string> {
        const routeState = this.resolveCurrentAuthRouteState();
        const parameters: Record<string, string> = {
            clientThumbprint: this.client.thumbprint,
            correlationId,
        };

        if (routeState.intent === AuthRouteIntent.ProvidedTenant && routeState.tenant) {
            parameters['tenant'] = routeState.tenant;
        }

        return parameters;
    }

    private resolveSwitchTenantRedirectStartPage(targetTenant: string): string | undefined {
        const currentPath = (this.router.url || '/').trim() || '/';
        const canonicalTargetPath = this.authRouteHelper.ensureTenantInUrl(currentPath, targetTenant);
        return canonicalTargetPath || undefined;
    }
}
